import { useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import cn from 'classnames';
import { ChevronDown, SearchIcon } from 'lucide-react';
import { REEBELO_DOMAINS } from '@lambda/reebelo';
import settings from '@/settings';
import i18n from '@/settings/i18n';
import {
  getAllCountries,
  getCurrentCountry,
  mapLocaleToLanguage,
} from './constants';

const t = i18n.init('shared');

// Case- and accent-insensitive, so "osterreich" finds "Österreich".
const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const getStoreLink = ({
  store,
  locale,
  locales,
}: {
  store: string;
  locale: 'en' | 'fr';
  locales: string[];
}) => {
  const protocol = process.env.NEXT_PUBLIC_STAGE === 'dev' ? 'http' : 'https';
  const domain = REEBELO_DOMAINS[store as keyof typeof REEBELO_DOMAINS];
  const langPath = locale === 'fr' && locales.length > 1 ? '/fr/' : '';

  return `${protocol}://${domain}${langPath}`;
};

const CountryFlagLabel = ({
  flag,
  name,
  bold,
}: {
  flag: string;
  name: string;
  bold?: boolean;
}) => (
  <div className="flex items-center gap-2">
    <span className="flex h-6 w-6 shrink-0 items-center">
      <Image
        src={flag}
        alt={name}
        width={24}
        height={24}
        className="h-full w-full object-contain"
      />
    </span>
    <span className={cn('text-sm text-gray-700', bold && 'font-bold')}>
      {name}
    </span>
  </div>
);

const LocaleLabel = ({ locale }: { locale: string }) => (
  <>{mapLocaleToLanguage(locale as 'fr' | 'de' | 'es' | 'en')}</>
);

const CountryList = ({
  onClose,
  onSelect,
  autoFocusSearch,
  listClassName,
}: {
  onClose: () => void;
  autoFocusSearch?: boolean;
  // Caps the list height so it scrolls under the fixed search input.
  listClassName?: string;
  onSelect?: (info: {
    store: string;
    locale: 'en' | 'fr';
    href: string;
  }) => void;
}) => {
  const [query, setQuery] = useState('');
  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  const router = useRouter();
  const currentLocale = router.locale || 'en';

  // Builds the href + click handler for a selectable (non-active) row.
  const buildLink = (store: string, locale: 'en' | 'fr', locales: string[]) => {
    const href = getStoreLink({ store, locale, locales });

    return {
      href,
      onClick: onSelect ? () => onSelect({ store, locale, href }) : undefined,
    };
  };

  const normalizedQuery = normalize(query);
  const allCountries = getAllCountries();
  const currentCountry = getCurrentCountry(settings.store);
  // The current store is pinned above the list, so it's only listed again
  // while searching - otherwise a search could hide or duplicate it.
  const showCurrent = currentCountry != null && !normalizedQuery;
  const countries = allCountries.filter((country) =>
    normalizedQuery
      ? [
          country.country,
          ...country.locales.map((locale) =>
            mapLocaleToLanguage(locale as 'fr' | 'de' | 'es' | 'en'),
          ),
        ].some((label) => normalize(label).includes(normalizedQuery))
      : country.store !== currentCountry?.store,
  );

  const renderCountry = (country: (typeof allCountries)[number]) => {
    const isActive = country.store === currentCountry?.store;
    const isMultiLocale = country.locales.length > 1;
    const isExpanded = expandedCode === country.code;

    const selectedLocale =
      isActive && country.locales.includes(currentLocale)
        ? currentLocale
        : country.locales[0];

    const link = buildLink(
      country.store,
      country.locales[0] as 'en' | 'fr',
      country.locales,
    );

    return (
      <li key={country.code} className="group">
        <div className="mx-2 border-b border-[#F0F3F7] group-first:border-b-0" />
        {isMultiLocale ? (
          <button
            type="button"
            onClick={() => setExpandedCode(isExpanded ? null : country.code)}
            className={cn(
              'flex w-full items-center justify-between rounded-lg p-3 ',
              {
                'bg-[#F0F3F7]': isActive,
              },
            )}
          >
            <CountryFlagLabel flag={country.flag} name={country.country} />
            <span className="flex items-center text-xs text-gray-700/70">
              <LocaleLabel locale={selectedLocale} />
              <ChevronDown
                className={cn(
                  'ml-1 h-4 w-4 transition-transform duration-200',
                  isExpanded && 'rotate-180',
                )}
              />
            </span>
          </button>
        ) : (
          <a
            href={isActive ? undefined : link.href}
            onClick={isActive ? onClose : link.onClick}
            className={cn(
              'flex w-full items-center justify-between rounded-lg p-3',
              {
                'bg-[#F0F3F7]': isActive,
                'hover:bg-[#F0F3F7]/50': !isActive,
              },
            )}
          >
            <CountryFlagLabel
              flag={country.flag}
              name={country.country}
              bold={isActive}
            />
            <span className="text-xs text-gray-700/70">
              <LocaleLabel locale={country.locales[0]} />
            </span>
          </a>
        )}
        {isMultiLocale && (
          <div
            className={cn(
              'grid overflow-hidden transition-[grid-template-rows] duration-200 ease-in-out',
              isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
            )}
          >
            <ul className="min-h-0 overflow-hidden">
              {country.locales.map((locale) => {
                const isActiveLocale = isActive && locale === currentLocale;
                const localeLink = buildLink(
                  country.store,
                  locale as 'en' | 'fr',
                  country.locales,
                );

                return (
                  <li key={locale}>
                    <a
                      href={isActiveLocale ? undefined : localeLink.href}
                      onClick={isActiveLocale ? onClose : localeLink.onClick}
                      className={cn(
                        'flex items-center rounded-lg py-2 pl-[45px] pr-4 text-xs text-gray-700',
                        {
                          'font-bold': isActiveLocale,
                          'hover:bg-[#F0F3F7]/50': !isActiveLocale,
                        },
                      )}
                    >
                      <LocaleLabel locale={locale} />
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="w-full">
      <div className="px-2 pb-2">
        <label className="flex h-10 items-center gap-2 rounded-full bg-gray-200 px-4 text-[13px] text-gray-700">
          <SearchIcon className="h-5 w-5 shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t`Search for a country`}
            aria-label={t`Search for a country`}
            autoFocus={autoFocusSearch}
            autoComplete="off"
            className="w-full border-0 bg-transparent p-0 text-base placeholder:text-gray-700/60 focus:outline-none focus:ring-0 lg:text-[13px]"
          />
        </label>
      </div>
      {showCurrent && (
        <section className="pb-2">
          <h4 className="px-3 pb-1 text-xs font-semibold text-gray-700">
            {t`Current country`}
          </h4>
          <ul className="w-full">{renderCountry(currentCountry)}</ul>
          <div className="mx-2 mt-2 border-b border-[#F0F3F7]" />
        </section>
      )}
      {countries.length === 0 ? (
        <p className="px-3 py-6 text-center text-sm text-gray-700/70">
          {t`No countries found`}
        </p>
      ) : (
        <ul
          className={cn(
            'w-full overflow-y-auto overscroll-contain',
            listClassName,
          )}
        >
          {countries.map(renderCountry)}
        </ul>
      )}
    </div>
  );
};

export default CountryList;
